// Sales (non-resident) leave rules mirrored for UX hints only.
// The backend is the single source of truth and re-checks everything.
// "Today" is always the project base date (UTC+3), never the browser date.
const BUSINESS_UTC_OFFSET_HOURS = 3
const HOUR_MS = 3600 * 1000
const DAY_MS = 24 * HOUR_MS

export const SALES_PAID_MAX_DAYS = 14
export const SALES_PAID_ANNUAL_DAYS = 30
export const PAID_CODE = 'paid_leave'
export const SICK_CODE = 'sick_leave'

export const SALES_LEAVE_SELF_ROLES = [
  'general_manager',
  'sales_manager',
  'sales_supervisor',
  'marketing_supervisor'
]
export const SALES_LEAVE_REVIEW_ROLES = ['general_manager', 'sales_supervisor']
export const SALES_LEAVE_VIEW_ROLES = [
  'general_manager',
  'sales_supervisor',
  'marketing_supervisor'
]

const toIso = date => date.toISOString().slice(0, 10)
const fromIso = iso => new Date(`${iso}T00:00:00Z`)

export function businessToday(now = Date.now()) {
  return toIso(new Date(now + (BUSINESS_UTC_OFFSET_HOURS * HOUR_MS)))
}

export function addDays(iso, days) {
  return toIso(new Date(fromIso(iso).getTime() + (days * DAY_MS)))
}

export function inclusiveDays(from, to) {
  return Math.round((fromIso(to) - fromIso(from)) / DAY_MS) + 1
}

export function newRequestStartRange(typeCode, today = businessToday()) {
  if (typeCode === SICK_CODE) {
    return { min: addDays(today, -1), max: today }
  }
  return { min: addDays(today, 1), max: null }
}

export function validateNewRequest({ type, from_date: from, to_date: to, reason }, today = businessToday()) {
  if (!type || !from || !to) return ''
  if (from >= to) return 'The start date must be earlier than the end date.'
  const range = newRequestStartRange(type.code, today)
  if (from < range.min || (range.max && from > range.max)) {
    if (type.code === SICK_CODE) return 'Sick leave must start yesterday or today.'
    return 'This leave must start no earlier than tomorrow.'
  }
  if (type.code === PAID_CODE && inclusiveDays(from, to) > SALES_PAID_MAX_DAYS) {
    return `Paid leave cannot exceed ${SALES_PAID_MAX_DAYS} calendar days.`
  }
  if (type.reason_required && !(reason || '').trim()) return 'Reason must not be blank.'
  return ''
}

export function changeScenario(leave, from, to) {
  if (from !== leave.from_date) return 'move_start'
  if (to > leave.to_date) return 'extend'
  return 'shorten'
}

export function validateChange(leave, { from_date: from, to_date: to, reason }, today = businessToday()) {
  if (!from || !to) return ''
  if (from >= to) return 'The start date must be earlier than the end date.'
  if (from === leave.from_date && to === leave.to_date) {
    return 'The requested dates are the same as the current dates.'
  }
  if (leave.to_date < today) return 'A leave that has already ended cannot be changed.'
  const scenario = changeScenario(leave, from, to)
  if (scenario === 'move_start') {
    if (leave.from_date <= today) return 'The start of a leave that has already begun cannot be changed.'
    if (from <= today) return 'The new start date must be later than today.'
  } else if (scenario === 'shorten' && to <= today) {
    return 'The new end date must be later than today.'
  }
  if (leave.type.code === PAID_CODE && inclusiveDays(from, to) > SALES_PAID_MAX_DAYS) {
    return `Paid leave cannot exceed ${SALES_PAID_MAX_DAYS} calendar days.`
  }
  if (!(reason || '').trim()) return 'Reason must not be blank.'
  return ''
}

// Which date pickers make sense for a change request of this leave.
export function changeDateLimits(leave, today = businessToday()) {
  const started = leave.from_date <= today
  return {
    canMoveStart: !started,
    minEnd: started ? addDays(today, 1) : null
  }
}

export function annualWarning(annual) {
  if (!annual || !annual.exceeded) return ''
  return annual.years
    .filter(year => year.exceeded)
    .map(year => `${year.year}: ${year.total_days} of ${annual.limit} days (exceeds by ${year.excess_days})`)
    .join('; ')
}

export function errorMessage(error) {
  const data = error && error.response && error.response.data
  if (data && data.details) return data.details
  if (data && data.detail) return data.detail
  return ''
}

export const REQUEST_KIND_LABELS = {
  create: 'New leave',
  change_dates: 'Date change',
  cancel_leave: 'Cancellation'
}

export const PERIOD_STATUS_LABELS = {
  upcoming: 'Upcoming',
  in_process: 'In progress',
  past: 'Completed',
  cancelled: 'Cancelled'
}
